/* eslint-disable jsx-a11y/anchor-is-valid */
import { useCallback, useEffect, useState } from "react"
import { Link } from 'react-router'
import { useAppDispatch, useAppSelector } from "../hooks/reduxToolkit"
import { ExpenseModel, deleteExpense, isCompleteExceptForIncidentID, isCompleteExpense, isTextractJobFailed, isTextractPending } from "../reduxToolkit/expensesSlice"
import { formatDateInTimeZone } from "../../lib/formatDateInTimeZone"
import { Attachment } from "./attachment"
import { present } from "../../lib/util/present"
import { Tooltip } from "./tooltip"
import { formatCurrency } from "../../lib/formatCurrency"
import { useCustomization } from "../hooks/useCustomizations"
import { hraCardDisplayLabel } from "../reduxToolkit/selectors/customizations"
import { Decimal } from "decimal.js"
import { CurrencyLedger } from "./currencyLedger"
import { maybeCalculateActualPaidAmountFromIncompleteExpense, maybeCalculateDiscountAmountFromIncompleteExpense, maybeCalculateRemainingOwedAmountFromIncompleteExpense } from "../../lib/expenses/amounts"
import { formatRequestDate, fiveBusinessDaysFrom } from "../../lib/util/itemizedBillRequestLogic"
import { ConversationRow } from "../../types/supabase"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { useAsyncAction } from "../hooks/useAsyncAction"
import { findItemizedBillConversation } from "../reduxToolkit/conversationsSelectors"
import { useClient } from "../providers/supabase"
import { SendNowButton } from "./sendNowButton"
import { deleteAttachment } from "../reduxToolkit/attachmentsSlice"
import { useConfirm } from "../providers/confirmDialog"
import { useTerminology } from "../hooks/useTerminology"
import { BankChargeChip, isPlaidCharge } from "./bankChargeChip"

import './expenseTable.scss'

const seconds = 1000

interface ExpenseDisplayOptions {
  hideIncident?: boolean
  hideProvider?: boolean

  highlightExpenseId?: string | null

  expandAll?: boolean
}

interface ExpenseTableProps {
  expenses: ExpenseModel[]

  displayOptions?: ExpenseDisplayOptions

  disableEdit?: boolean
}

export function ExpenseTable({
  expenses,
  displayOptions,
  disableEdit,
}: ExpenseTableProps) {
  const highlightedExpense = expenses.find((e) => e.id === displayOptions?.highlightExpenseId)
  const expenseIds = expenses.map((e) => e.id)
  const client = useClient()

  // Get conversation IDs from expenses that have itemized_bill_conversation_id set
  const expenseConversationIds = expenses
    .map((e) => e.itemized_bill_conversation_id)
    .filter((id): id is string => !!id)

  // Itemized-bill conversations are keyed on the INCIDENT, not the expense -- see
  // findItemizedBillConversation. This used to fetch `topic_ref_id.in.(expenseIds)` as a
  // "legacy way" alongside itemized_bill_conversation_id; that branch matched only rows the
  // React AI-call screen had written with the wrong key, and
  // 20260826000000_rekey_itemized_bill_conversations_to_incident.sql re-keys those.
  const incidentIds = [...new Set(expenses.map((e) => e.incident_id).filter((id): id is string => !!id))]

  const [conversationsResult, reloadConversations] = useAsyncAction(async () => {
    const allConversationIds = [...new Set([...expenseConversationIds])]

    let query = client
      .from('conversations')
      .select('*')

    if (allConversationIds.length > 0 && incidentIds.length > 0) {
      query = query.or(`id.in.(${allConversationIds.join(',')}),and(topic_type.eq.itemized-bill,topic_ref_id.in.(${incidentIds.join(',')}))`)
    } else if (allConversationIds.length > 0) {
      query = query.in('id', allConversationIds)
    } else if (incidentIds.length > 0) {
      query = query.eq('topic_type', 'itemized-bill').in('topic_ref_id', incidentIds)
    } else {
      return [] as ConversationRow[]
    }

    const conversations = await query

    if (conversations.error) {
      throw new Error(`Error fetching conversations: ${conversations.error.message}`)
    }

    return conversations.data as ConversationRow[]
  }, [incidentIds.join(','), expenseConversationIds.join(',')])

  // Load conversations on mount
  useAsyncEffect(async () => {
    await reloadConversations()
  }, [reloadConversations])

  const conversations = conversationsResult.data

  const handleConversationUpdate = useCallback(async () => {
    await reloadConversations()
  }, [reloadConversations])

  // Helper to find the itemized-bill conversation for an expense. One incident and provider
  // get one thread, so the sort that used to disambiguate several expense-keyed rows is gone.
  const findConversationForExpense = (expense: ExpenseModel) => {
    if (!conversations) return undefined
    return findItemizedBillConversation(conversations, expense, expense.provider_id)
  }

  return <div className="expense-table">
    {highlightedExpense &&
      <ExpenseRow expense={highlightedExpense}
        displayOptions={displayOptions}
        disableEdit={disableEdit}
        latestConversation={findConversationForExpense(highlightedExpense)}
        onConversationUpdate={handleConversationUpdate}
        />}

    {expenses.slice().sort(byDateNullsFirst).map((expense) => {
      if (displayOptions?.highlightExpenseId === expense.id) {
        return null
      }

      return <ExpenseRow key={expense.id}
        expense={expense}
        displayOptions={displayOptions}
        disableEdit={disableEdit}
        latestConversation={findConversationForExpense(expense)}
        onConversationUpdate={handleConversationUpdate}
        />
    })}
  </div>
}

export interface ExpenseRowProps {
  expense: ExpenseModel,

  displayOptions?: ExpenseDisplayOptions
  disableEdit?: boolean
  latestConversation?: ConversationRow
  onConversationUpdate?: () => Promise<void>
}

export function ExpenseRow({
  expense,
  displayOptions,
  disableEdit,
  latestConversation,
  onConversationUpdate
}: ExpenseRowProps) {
  const {id, date, paidAmount, provider, submitted_at} = expense
  const { highlightExpenseId } = displayOptions || {}
  const hra = useCustomization('hra')
  const terms = useTerminology()

  // Check for duplicate-detected todo
  const hasDuplicateTodo = useAppSelector((s) =>
    s.todos.todos?.some((t) => t.record_id === expense.id && t.todo_type === 'duplicate-detected' && !t.completed_at)
  )

  const providerAuthInfo = useAppSelector((s) => {
    const provider = s.providers.providers.find((p) => p.id === expense.provider_id)
    if (!provider) {
      return null
    }
    return {
      isAuthorized: present(provider.communication_authorized_at),
      providerHasBillingEmail: present(provider.billing_email)
    }
  })

  // Check if email was already sent - either via itemized_bill_conversation_id or latestConversation
  const hasExistingConversation = present(expense.itemized_bill_conversation_id) || present(latestConversation);
  const isEligibleForAutomaticRequest = providerAuthInfo &&
    providerAuthInfo.isAuthorized &&
    providerAuthInfo.providerHasBillingEmail &&
    !hasExistingConversation &&
    present(expense.date);

  // The email will be sent automatically five business days after the TODO is logged
  // This gives the user time to get the itemized bill their own way first
  // And gives the provider time to prepare the itemized bill
  const itemizedBillTodo = useAppSelector((s) => s.todos.todos?.find((t) => t.record_id === expense.id && t.todo_type === 'itemized-bill'))
  const nextRequestDate = itemizedBillTodo && present(itemizedBillTodo.created_at) && !present(itemizedBillTodo.completed_at) ?
    fiveBusinessDaysFrom(new Date(itemizedBillTodo.created_at)) : undefined

  const [expanded, setExpanded] = useState(displayOptions?.expandAll || highlightExpenseId === id)

  const isIncomplete = !isCompleteExpense(expense)
  const textractPending = isTextractPending(expense)
  const textractFailed = isTextractJobFailed(expense)
  const isDraft = expense.is_draft === true

  const classes = [
    `expense-table__row col-12 card`,
    highlightExpenseId === id && 'highlighted',
    submitted_at && 'submitted',
    expanded && 'expanded',
    textractPending && !textractFailed && 'scanning',
    isIncomplete && !textractPending && !textractFailed && 'incomplete',
    isDraft && 'draft',
  ].filter(present)
  
  let alert: JSX.Element | undefined
  if (textractPending && !textractFailed) {
    alert = <Tooltip tooltip="Scanning expense to extract information.  This can sometimes take a few minutes.  You can close this and come back later or click the edit button to input information manually.">
      <i className="material-icons flicker">sensors</i>
    </Tooltip>
  } else if (isIncomplete && !isDraft && Date.parse(expense.created_at) < (Date.now() - 10 * seconds)) {
    alert = <Tooltip tooltip={textractFailed ? "Unable to extract expense information" : "This expense is missing some information"}>
      <i className="material-icons text-danger">warning</i>
    </Tooltip>
  } else if (hasDuplicateTodo) {
    alert = <Tooltip tooltip="This expense may be a duplicate. Review and delete if it's a duplicate, or mark it as 'not a duplicate' in the edit screen.">
      <i className="material-icons text-warning">content_copy</i>
    </Tooltip>
  }
  
  let showIncidentAboveTheLine = true
  let showProviderAboveTheLine = false
  if (displayOptions?.hideIncident) {
    showProviderAboveTheLine = true
    showIncidentAboveTheLine = false
  }

  return <div id={`expense-table-row_${expense.id}`} className="row">
    <div className={classes.join(' ')}>
      <div className="card-body">
        <div  className={`expense-table__row-header ${expanded && 'expanded'}`} onClick={!expanded ? ((e) => {e.preventDefault(); e.stopPropagation(); setExpanded(true) }) : undefined}>
          <div className="card-title">
            <h5>
              {date ? formatDateInTimeZone(date, { format: 'MM/dd/yyyy' }) : 'Unknown Date'}
              &nbsp;-&nbsp;
              {paidAmount ? formatCurrency(paidAmount) : 'Unknown Amount'}
            </h5>
            
            <div className="expense-table-row__badges">
              {isDraft && (
                <Tooltip tooltip="This expense was automatically imported from your connected HSA account. Edit it to assign it to an incident.">
                  <span className="badge bg-info">DRAFT</span>
                </Tooltip>
              )}
              {expense.paid_with_hra &&
                <Tooltip tooltip={`This expense was paid for with your HRA card.  It will only be included if this ${terms.incident} gets submitted to CHM.`}>
                  <span className="badge bg-secondary">{hraCardDisplayLabel(hra, 'HRA CARD')}</span>
                </Tooltip>}

              {alert}
            </div>
          </div>
          
          {!displayOptions?.hideProvider && showProviderAboveTheLine && <ProviderLineItem expense={expense} />}
          {!displayOptions?.hideIncident && showIncidentAboveTheLine && present(expense.incident_id) && <IncidentLineItem expense={expense} />}
          
          {/* Show automatic request info if eligible */}
          {isEligibleForAutomaticRequest && nextRequestDate && nextRequestDate > new Date() && (
            <div className="text-muted small mt-1 d-flex align-items-center">
              <i className="material-icons" style={{ fontSize: '14px', verticalAlign: 'middle' }}>schedule</i>
              &nbsp;Will automatically request itemized bill on {formatRequestDate(nextRequestDate)}
              {expanded && (
                <SendNowButton 
                  expenseId={expense.id}
                  onSend={onConversationUpdate || (() => {})}
                />
              )}
            </div>
          )}

          <button className="btn btn-link expense-table__row-collapse-toggle"
              aria-expanded={expanded} aria-controls={`#expense-table-${id}`}
              onClick={(e) => {e.preventDefault(); e.stopPropagation(); setExpanded(!expanded) }}
              >
              <i className={`material-icons`}>{expanded ? 'expand_less' : 'expand_more'}</i>
          </button>
        </div>

        <div className={`expense-table__row-collapse collapse ${expanded && 'show'} card-text`} id={`expense-table-${id}`}
            data-id={id}>
          <ExpenseWidget expense={expense}
            displayOptions={displayOptions}
            expanded={expanded}
            disableEdit={disableEdit}
            showIncidentAboveTheLine={showIncidentAboveTheLine}
            showProviderAboveTheLine={showProviderAboveTheLine}
            latestConversation={latestConversation}
            />
        </div>
      </div>
    </div>
  </div>
}

interface ExpenseWidgetProps {
  expense: ExpenseModel

  displayOptions?: ExpenseDisplayOptions
  expanded?: boolean
  disableEdit?: boolean
  showIncidentAboveTheLine?: boolean
  showProviderAboveTheLine?: boolean
  latestConversation?: ConversationRow
}

function ExpenseWidget({
  expense,
  displayOptions,
  expanded,
  disableEdit,
  showIncidentAboveTheLine,
  showProviderAboveTheLine,
  latestConversation
}: ExpenseWidgetProps) {
  const {id, listedAmount, paidAmount: postDiscountAmount, patient_name, payment_history, is_fully_paid} = expense

  const matchedCharges = (payment_history?.payments ?? []).filter(isPlaidCharge)

  const dispatch = useAppDispatch()
  const [confirm] = useConfirm()
  const terms = useTerminology()
  const attachments = useAppSelector((s) => s.attachments.attachments?.filter((a) => a.record_id === expense.id) || [])

  const [loadAttachments, setLoadAttachments] = useState(expanded)
  useEffect(() => {
    if (expanded) {
      setLoadAttachments(true)
    }
  }, [expanded])

  const deleteExpenseWithConfirm = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();

    const confirmed = await confirm({
      title: 'Delete Expense',
      message: 'Are you sure you want to delete this expense?',
      actions: [
        {
          title: 'Cancel',
          style: 'secondary',
          isCancel: true,
        },
        {
          title: 'Delete',
          style: 'danger',
          isConfirm: true,
        }
      ]
    })

    if (confirmed) {
      const now = new Date().toISOString()

      // Delete all attachments for this expense
      attachments.forEach((attachment) => {
        dispatch(deleteAttachment({
          id: attachment.id,
          updated_at: now,
          deleted_at: now,
        }))
      })

      // Delete the expense
      dispatch(deleteExpense({ id, updated_at: now, deleted_at: now }))
    }
  }, [dispatch, id, attachments, confirm])


  const isIncomplete = !isCompleteExpense(expense)
  const isDraft = expense.is_draft === true



  let alerts: JSX.Element[] = []
  if (isIncomplete && !isDraft && !isTextractPending(expense) && Date.parse(expense.created_at) < (Date.now() - 10 * seconds)) {
    if (disableEdit) {
      alerts.push(
        <Tooltip tooltip={'This expense cannot be edited'}>
          {isCompleteExceptForIncidentID(expense) ?
            `This expense is not assigned to ${terms.incident === 'incident' ? 'an' : 'a'} ${terms.incident}.` :
            'Unable to automatically extract all expense information!'}
        </Tooltip>
      )
    } else {
      alerts.push(
        <Link to={`/expenses/${expense.id}/edit`}
          className={`card-link btn btn-outline-info`}
          state={{
            validateOnMount: true
          }}>
        {isCompleteExceptForIncidentID(expense) ?
          `This expense is not assigned to ${terms.incident === 'incident' ? 'an' : 'a'} ${terms.incident}.  Click here to fix that.` :
          'Unable to automatically extract all expense information!  Click here to fix that.'}
        </Link>
      )
    }
  }
  
  const remainingOwedAmount = maybeCalculateRemainingOwedAmountFromIncompleteExpense(expense)

  return <div className="expense-table__widget">
    {alerts}

    {latestConversation && (
      <Link to={`/conversations/${latestConversation.id}`} className="expense-table__widget-conversation text-muted small mt-1 d-flex align-items-center">
        <i className="material-icons" style={{ fontSize: '14px', verticalAlign: 'middle' }}>email</i>
        <span className="ms-1 d-flex w-100">
          {latestConversation.last_message_direction === 'INBOUND' ? 'Received' : 'Sent'}&nbsp;{latestConversation?.last_message_at ? formatDateInTimeZone(latestConversation.last_message_at, { format: 'MM/dd/yyyy' }) : 'Unknown Date'}
          <i className="material-icons ms-auto expense-table__widget-conversation__icon">arrow_right_alt</i>
        </span>
      </Link>
    )}

    {!displayOptions?.hideIncident && present(expense.incident_id) && !showIncidentAboveTheLine && <div className="row">
      <div className="col">
        <IncidentLineItem expense={expense} />
      </div>
    </div>}
    {!displayOptions?.hideProvider && present(expense.provider_id) && !showProviderAboveTheLine && <div className="row">
      <div className="col">
        <ProviderLineItem expense={expense} />
      </div>
    </div>}

    <div className="row mt-4">
      <div className="col-6 col-md-4">
        <h6 className="expense-table__widget__details__title">Details</h6>
        <CurrencyLedger
          className="expense-table__widget__details"
          rows={[
          { description: 'Listed', amounts: listedAmount ? [new Decimal(listedAmount)] : undefined, rowClass: 'expense-table__widget__details__listed' },
          { description: 'Discount', amounts: [maybeCalculateDiscountAmountFromIncompleteExpense(expense)], rowClass: 'expense-table__widget__details__discount' },
          { description: 'Paid', amounts: [maybeCalculateActualPaidAmountFromIncompleteExpense(expense)], rowClass: 'expense-table__widget__details__paid' },
          {
            description: 'Owed',
            amounts: remainingOwedAmount ? [remainingOwedAmount] : undefined,
            rowClass: `expense-table__widget__details__owed ${remainingOwedAmount && !remainingOwedAmount.eq(0) ? 'expense-table__widget__details__owed--nonzero' : ''}`
          }
        ]} />

        {matchedCharges.length > 0 && (
          <div className="expense-table__widget__matched-charges">
            <Tooltip tooltip="Charges automatically matched from your connected bank account.">
              <div className="expense-table__widget__matched-charges__title">Matched charges</div>
            </Tooltip>
            <div className="expense-table__widget__matched-charges__list d-flex flex-column gap-1">
              {matchedCharges.map((p, i) => (
                <BankChargeChip
                  key={p.plaid_transaction_id ?? `charge-${p.date}-${p.amount}-${i}`}
                  payment={p}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="col-6 col-md-8 expense-table__widget__attachments">
        <h6 className="expense-table__widget__attachments__title">Attachments</h6>
        <div className="expense-table__widget__attachments__list">
          {
            loadAttachments && attachments.map((attachment) => {
              return <Attachment key={attachment.id} attachment={attachment} />
            })
          }
        </div>
      </div>
    </div>
    {!disableEdit && <div className="expense-table__widget__actions">
      <Link to={`/expenses/${expense.id}/edit`}
          className={`card-link btn btn-outline-info ${disableEdit ? 'disabled' : ''}`}
          onClick={disableEdit ? (e) => {e.preventDefault(); e.stopPropagation()} : undefined}>
        Edit
      </Link>
      &nbsp;
      <button className="btn btn-outline-danger" onClick={deleteExpenseWithConfirm}>Delete</button>
    </div>}
  </div>
}

function ProviderLineItem({ expense }: { expense: ExpenseModel }) {
  if (!present(expense.provider)) {
    return null
  }
  

  return <Link to={expense.provider_id ? `/providers/${expense.provider_id}` : `/expenses/${expense.id}/providers/new`}
          className="card-subtitle expense-table__row-provider">
      <h5 className="overline">Provider:</h5>
      <span className="d-flex w-100">
        {expense.provider}
        <i className="material-icons ms-auto expense-table__widget-incident__icon">arrow_right_alt</i>
      </span>
    </Link>
}

export function IncidentLineItem({ expense }: { expense: ExpenseModel }) {
  const incident = useAppSelector((s) => s.incidents.incidents.find((i) => i.id === expense.incident_id))
  const terms = useTerminology()

  return <Link to={`/incidents/${expense.incident_id}`} className="expense-table__widget-incident">
      <h5 className="overline">{terms.Incident}</h5>
      <span className="d-flex w-100">
      {incident?.description}
      <i className="material-icons ms-auto expense-table__widget-incident__icon">arrow_right_alt</i>
      </span>
    </Link>
}

function byDateNullsFirst(a: { date?: string | null }, b: { date?: string | null }) {
  if (!present(a.date)) {
    return -1
  }
  if (!present(b.date)) {
    return 1
  }
  
  return Date.parse(b.date) - Date.parse(a.date)
}



